import {AppointmentBookingEnum} from 'main/analytics/gaTagsEnum';
import {ChatRoomsEnum} from 'presentation/constants';
import {useHostName} from 'presentation/hooks';
import {UNITS_CHANNELS_CONTACTS} from 'presentation/modules/booking/shared/constants/unitsChannelsContacts';
import {MAX_DISTANCE_FILTER_UNITS_ODONTO} from 'presentation/modules/booking/shared/constants/unitsInfo';
import {useBookingSummary} from 'presentation/modules/booking/shared/hooks/useBookingSummary';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useOpenDrawerFilterStore} from 'presentation/modules/booking/shared/store/useOpenDrawerFilterStore';
import {ChangeFlowVariantEnum} from 'presentation/modules/booking/shared/ui/components/ChangeFlowDialog/constants';
import {useCallChatRoom, useMediaQueries, useQueryParams} from 'presentation/pages';

import {translations} from 'presentation/translations-new';
import React, {useState} from 'react';

const {unitsFlow} = translations['pt-br'].privateFlow.bookingFlow;
const {choosePhoneNumber} = translations['pt-br'].privateFlow.bookingFlow.unitsFlow.unitDetails;
const {changeAppointmentLocal} = translations['pt-br'].privateFlow.bookingFlow.unitsFlow;

export function useOdontoUnitsNotFoundState() {
	const {isMobile} = useMediaQueries();
	const {SEARCH_PARAMS} = useQueryParams();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [openPhoneDrawerState, setOpenPhoneDrawerState] = useState(false);
	const {callChatRoom, IS_LOADING: callChatRoomIsLoading} = useCallChatRoom();
	const {dataHostNameState} = useHostName();
	const {handleChangeModals} = useBookingSummary();
	const setOpenDrawerState = useOpenDrawerFilterStore(state => state.setOpenDrawerState);
	const {isManualLocation} = useBookingStore(state => state.bookingData);
	const {localityDistance} = SEARCH_PARAMS;

	const distanceFilterAtMaxDistance =
		Number(localityDistance) >= MAX_DISTANCE_FILTER_UNITS_ODONTO;
	const isPhoneNumberMenuOpen = Boolean(anchorEl);
	const phoneNumbersAlert = UNITS_CHANNELS_CONTACTS[dataHostNameState.product].alert;
	const phoneNumbers = UNITS_CHANNELS_CONTACTS[dataHostNameState.product].phones;

	const handleOpenPhoneMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
		if (isMobile) {
			setOpenPhoneDrawerState(true);
			return;
		}
		setAnchorEl(event.currentTarget);
	};

	const getEmptyDescription = () => {
		if (isManualLocation) {
			return unitsFlow.notFoundModal.descriptionOdonto;
		}

		return distanceFilterAtMaxDistance
			? unitsFlow.notFoundModal.descriptionOdonto
			: unitsFlow.notFoundModal.description;
	};

	const emptyDescription = getEmptyDescription();

	const handleTogglePhoneDrawer = () => {
		setOpenPhoneDrawerState(prev => !prev);
	};

	const handleClosePhoneMenu = () => {
		setAnchorEl(null);
	};

	const handleOpenDrawer = () => {
		setOpenDrawerState(true);
	};

	const handleChangeLocation = () => handleChangeModals(ChangeFlowVariantEnum.LOCATION);

	const handleCallChatRoom = async () => {
		await callChatRoom(ChatRoomsEnum.ODONTO);
	};

	const handlePrimaryButtonAction = () => {
		if (isManualLocation) {
			return handleChangeLocation;
		}

		return distanceFilterAtMaxDistance ? handleChangeLocation : handleOpenDrawer;
	};

	const primaryButtonLabel = () => {
		if (isManualLocation) {
			return changeAppointmentLocal;
		}

		return distanceFilterAtMaxDistance
			? changeAppointmentLocal
			: unitsFlow.searchNearbyUnitsHealthExam;
	};

	const primaryButtonDataTagId = () => {
		if (isManualLocation) {
			return AppointmentBookingEnum.CLICK_ALTERAR_LOCAL_ATENDIMENTO_AGENDAMENTO;
		}

		return distanceFilterAtMaxDistance
			? AppointmentBookingEnum.CLICK_ALTERAR_LOCAL_ATENDIMENTO_AGENDAMENTO
			: AppointmentBookingEnum.CLICK_BUSCAR_UNIDADES_PROXIMAS;
	};

	const primaryButtonConfig = {
		label: primaryButtonLabel(),
		action: handlePrimaryButtonAction(),
		dataTagId: primaryButtonDataTagId(),
	};

	return {
		emptyDescription,
		primaryButtonConfig,
		handleCallChatRoom,
		handleOpenPhoneMenu,
		isMobile,
		openPhoneDrawerState,
		handleTogglePhoneDrawer,
		choosePhoneNumber,
		phoneNumbersAlert,
		phoneNumbers,
		isPhoneNumberMenuOpen,
		anchorEl,
		handleClosePhoneMenu,
		callChatRoomIsLoading,
	};
}
